import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '../cn.js';
import { STATUS_GLYPH } from '../generated/status.js';
import { Button } from './button.js';
import { CloseMark, StatusGlyph } from './icons.js';
/**
 * The modal (§06).
 *
 * Radix carries the focus trap, the restore-on-close, the escape handling and
 * the `aria-modal` wiring — all of it fiddly, all of it load-bearing for anyone
 * on a keyboard, and none of it worth re-implementing.
 *
 * `description` is required rather than optional. A dialog whose only text is a
 * heading and two buttons is a question a screen reader user cannot hear, and
 * making it optional is how it ends up missing on the one that voids a claim.
 */
const TONE_TILE = {
    verified: 'bg-verified-bg text-verified',
    scheduled: 'bg-scheduled-bg text-scheduled',
    progress: 'bg-progress-bg text-progress',
    warning: 'bg-warning-bg text-warning',
    critical: 'bg-critical-bg text-critical',
    inactive: 'bg-inactive-bg text-inactive',
};
export function Modal({ open, onOpenChange, title, description, 
/**
 * What kind of thing this dialog is about, shown as the tone's own glyph in
 * a tinted tile beside the title — the reference's treatment for a dialog
 * raised by a problem.
 *
 * Optional because most dialogs are not about a status. When it is set, the
 * tile is `aria-hidden`: the tone is already in the title, and a dialog that
 * needs an icon to say it is serious has the wrong title.
 */
tone, children, footer, className, }) {
    return (_jsx(Dialog.Root, { open: open, onOpenChange: onOpenChange, children: _jsxs(Dialog.Portal, { children: [_jsx(Dialog.Overlay, { className: "bg-overlay fixed inset-0" }), _jsxs(Dialog.Content, { className: cn('bg-surface border-line fixed left-1/2 top-1/2 w-[min(34rem,calc(100vw-2rem))]', 'shadow-e4 -translate-x-1/2 -translate-y-1/2 rounded-lg border p-6', className), children: [_jsxs("div", { className: "flex items-start justify-between gap-4", children: [_jsxs("div", { className: "flex min-w-0 items-start gap-3", children: [tone && (_jsx("span", { "aria-hidden": "true", className: cn('h-tile w-tile grid flex-none place-items-center rounded-md', TONE_TILE[tone]), children: _jsx(StatusGlyph, { name: STATUS_GLYPH[tone], className: "h-5 w-5" }) })), _jsxs("div", { className: "min-w-0", children: [_jsx(Dialog.Title, { className: "text-ink text-lg font-semibold", children: title }), _jsx(Dialog.Description, { className: "text-ink-3 mt-1 text-sm", children: description })] })] }), _jsx(Dialog.Close, { asChild: true, children: _jsx(Button, { variant: "ghost", size: "sm", "aria-label": "Close", icon: _jsx(CloseMark, {}) }) })] }), children && _jsx("div", { className: "mt-5", children: children }), footer && _jsx("div", { className: "mt-6 flex justify-end gap-3", children: footer })] })] }) }));
}
//# sourceMappingURL=modal.js.map